import { Box, Button, Group, Text } from '@mantine/core';
import type { Zone } from '@seo/contracts';
import { publicMessageOf } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from '../pages/EntityPage.module.css';

/**
 * Форма створення над реєстром.
 *
 * З'явилась тому, що реєстри були **тільки для читання**: екран брендів радив «Створіть
 * перший — сайт без сутності не збереться», а кнопки створення на ньому не було взагалі.
 * API вміє `create` з першого дня, RBAC має зону `write` — не було саме екрана.
 *
 * Панель, а не модалка: у застосунку немає інфраструктури модалок, а наявні форми
 * (додавання бонусу, план лінків) — інлайнові. Один патерн на всі шість реєстрів краще за
 * шість різних, і саме тому це спільний компонент, а не копія в кожному файлі.
 *
 * Стан «відкрито» контролює сторінка: закрити панель має **успіх мутації**, а не клік, і
 * лише сторінка знає, коли він стався.
 */
export interface CreatePanelProps {
  /** Напис на кнопці, що розкриває форму: «Додати бренд». */
  label: string;
  /** Префікс для `data-testid`: `brand` → `create-brand-open`, `-form`, `-submit`, `-error`. */
  testId: string;
  /**
   * Зона прав, у якій живе створення. Панель питає матрицю сама.
   *
   * Доктрина проєкту — «UI ховає те, у чому API відмовить» (`docs/DOMAIN.md`, `nav.ts`), і
   * для цього в застосунку вже був хук `useCan`… якого не використовував ніхто. Роль
   * `linkbuilder` має `databases: read`, тобто бачила реєстр, кнопку «Додати», форму — і
   * лише на «Створити» отримувала FORBIDDEN. Питати матрицю тут означає, що жоден із шести
   * екранів не може про це забути.
   */
  zone: Zone;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy?: boolean;
  /** Помилка мутації. Показується під формою текстом, зрозумілим оператору. */
  error?: unknown;
  /**
   * Скидання форми й стану мутації. Викликається щоразу, коли панель закривається.
   *
   * Без нього react-query тримає `error` до наступного `mutate()`, а Mantine-форма —
   * старі значення: після відмови сервера, «Скасувати» й повторного відкриття оператор
   * бачив червону помилку над уже заповненим полем, нічого не натиснувши. Перевірено
   * рантайм-зондом на шести екранах — поведінка була однакова скрізь, тому скидання живе
   * тут, а не шістьма копіями по сторінках.
   */
  onReset?: () => void;
  onSubmit: (event: React.SyntheticEvent<HTMLFormElement>) => void;
  children: React.ReactNode;
}

export function CreatePanel({
  label,
  testId,
  zone,
  open,
  onOpenChange,
  busy = false,
  error,
  onReset,
  onSubmit,
  children,
}: CreatePanelProps): React.JSX.Element {
  const canWrite = useCan(zone, 'write');

  const close = (): void => {
    onOpenChange(false);
    onReset?.();
  };

  // Немає права на запис — немає й кнопки. Відмова API лишається останнім рубежем, але
  // оператор не мусить дізнаватись про неї, заповнивши форму.
  if (!canWrite) return <></>;

  if (!open) {
    return (
      <Button
        size="xs"
        data-testid={`create-${testId}-open`}
        onClick={() => {
          onOpenChange(true);
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <Box className={classes.panel} data-testid={`create-${testId}-panel`}>
      <form onSubmit={onSubmit} data-testid={`create-${testId}-form`} noValidate>
        <Group gap="sm" align="flex-start" wrap="wrap">
          {children}
          <Button type="submit" size="xs" loading={busy} data-testid={`create-${testId}-submit`}>
            Створити
          </Button>
          <Button
            type="button"
            size="xs"
            variant="subtle"
            data-testid={`create-${testId}-cancel`}
            onClick={close}
          >
            Скасувати
          </Button>
        </Group>
        {error == null ? null : (
          <Text
            className={classes.danger}
            mt={6}
            data-testid={`create-${testId}-error`}
            role="alert"
          >
            {publicMessageOf(error)}
          </Text>
        )}
      </form>
    </Box>
  );
}
